import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Building2 } from 'lucide-react';
import { COMPANY_SIZES, INDUSTRIES } from '@/lib/calculationEngine';
import { ProvenanceBadge } from '@/components/trust/ProvenanceBadge';
import { LabeledSlider } from './fields/LabeledSlider';

const valueBadge = (value, testId) => (
  <span
    className="text-sm text-[#527F19]"
    style={{ fontFamily: 'IBM Plex Mono, monospace' }}
    data-testid={testId}
  >
    {value}
  </span>
);

export function CompanyProfile({ config, updateConfig }) {
  const { t } = useTranslation();
  return (
    <Card className="bg-white border-[#DDE1E5]" data-testid="company-profile-section">
      <CardHeader className="pb-4">
        <CardTitle
          className="flex items-center gap-2 text-lg text-[#171A1D]"
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        >
          <Building2 className="w-5 h-5 text-[#66707A]" />
          {t('companyProfile.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label className="text-[#66707A] text-sm">{t('companyProfile.companySize')}</Label>
            <Select value={config.companySize} onValueChange={v => updateConfig({ companySize: v })}>
              <SelectTrigger aria-label={t('companyProfile.companySize')} className="mt-1.5 bg-white border-[#DDE1E5] text-[#171A1D]" data-testid="company-size-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#DDE1E5]">
                {COMPANY_SIZES.map(s => (
                  <SelectItem key={s.value} value={s.value} className="text-[#171A1D] focus:bg-[#F5F6F7] focus:text-[#171A1D]">
                    {t('calcData.sizes.' + s.value)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[#66707A] text-sm">{t('companyProfile.industry')}</Label>
            <Select value={config.industry} onValueChange={v => updateConfig({ industry: v })}>
              <SelectTrigger aria-label={t('companyProfile.industry')} className="mt-1.5 bg-white border-[#DDE1E5] text-[#171A1D]" data-testid="industry-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#DDE1E5]">
                {INDUSTRIES.map(i => (
                  <SelectItem key={i.value} value={i.value} className="text-[#171A1D] focus:bg-[#F5F6F7] focus:text-[#171A1D]">
                    {t('calcData.industries.' + i.value)} ({i.multiplier}x)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <LabeledSlider
          label={t('companyProfile.locations')}
          value={config.locations}
          onValueChange={v => updateConfig({ locations: v })}
          min={1}
          max={20}
          step={1}
          display={valueBadge(config.locations, 'locations-value')}
          testId="locations-slider"
        />

        <LabeledSlider
          label={t('companyProfile.itSystems')}
          value={config.itSystems}
          onValueChange={v => updateConfig({ itSystems: v })}
          min={1}
          max={50}
          step={1}
          display={valueBadge(config.itSystems, 'it-systems-value')}
          testId="it-systems-slider"
        />

        {/* Optional planning inputs — each one raises estimate confidence and
            replaces a model assumption with an organisation-specific value. */}
        <div className="pt-4 border-t border-[#DDE1E5] grid sm:grid-cols-2 gap-4">
          <div>
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="employees-input" className="text-[#66707A] text-sm">
                {t('trust.mPersonnel')}
              </Label>
              <ProvenanceBadge provenance={config.employees ? 'USER_PROVIDED' : 'MODELLED'} />
            </div>
            <Input
              id="employees-input"
              type="number"
              min={1}
              max={100000}
              inputMode="numeric"
              placeholder={t('trust.notSet')}
              className="mt-1.5 bg-white border-[#DDE1E5] text-[#171A1D] rounded-md"
              value={config.employees ?? ''}
              onChange={e => updateConfig({ employees: e.target.value === '' ? null : Math.max(0, Number(e.target.value)) })}
              data-testid="employees-input"
            />
            <p className="mt-1 text-xs text-[#8A929B]">{t('companyProfile.employeesHint')}</p>
          </div>
          <div>
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="target-months-input" className="text-[#66707A] text-sm">
                {t('trust.targetMonthsLabel')}
              </Label>
              <ProvenanceBadge provenance={config.targetMonths ? 'USER_PROVIDED' : 'DEFAULT_ASSUMPTION'} />
            </div>
            <Input
              id="target-months-input"
              type="number"
              min={1}
              max={120}
              inputMode="numeric"
              placeholder={t('trust.notSet')}
              className="mt-1.5 bg-white border-[#DDE1E5] text-[#171A1D] rounded-md"
              value={config.targetMonths ?? ''}
              onChange={e => updateConfig({ targetMonths: e.target.value === '' ? null : Math.max(0, Number(e.target.value)) })}
              data-testid="target-months-input"
            />
            <p className="mt-1 text-xs text-[#8A929B]">{t('trust.targetMonthsHint')}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
